import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Download, Trash2, LogOut, Users } from "lucide-react";
import { TOKEN_KEY } from "./AdminLogin";
import { LANGUAGES } from "../i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ["new", "invited", "contacted"];
const STATUS_FILTERS = ["all", ...STATUSES];

const statusStyle = {
  new: "bg-reveal-rose/15 text-reveal-rose",
  invited: "bg-blue-500/15 text-blue-300",
  contacted: "bg-green-500/15 text-green-300",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (languageFilter !== "all") params.language = languageFilter;
      const { data } = await axios.get(`${API}/admin/waitlist`, { ...authHeader, params });
      setEntries(data.entries);
      setTotal(data.total);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load waitlist");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, languageFilter]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, languageFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/waitlist/${id}`, { status }, authHeader);
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Delete ${email} from the waitlist?`)) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, authHeader);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => t - 1);
      toast.success("Entry deleted");
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, { ...authHeader, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "reveal_waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-10" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl text-reveal-cream">Waitlist</h1>
            <p className="text-sm text-reveal-cream/55 mt-1 flex items-center gap-2">
              <Users className="w-4 h-4 text-reveal-rose" />
              <span data-testid="admin-total-count">{total}</span> total signups
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-5 py-2.5 rounded-full hover:bg-[#c9987b] transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-reveal-rose/40 text-reveal-cream px-5 py-2.5 rounded-full hover:bg-reveal-rose/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-reveal-cream/40 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, country, referral..."
              data-testid="admin-search-input"
              className="w-full bg-black/40 border border-reveal-rose/30 rounded-full pl-11 pr-4 py-2.5 text-reveal-cream placeholder:text-reveal-cream/40 outline-none focus:border-reveal-rose transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${
                  statusFilter === s
                    ? "bg-reveal-rose text-black"
                    : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            data-testid="admin-language-filter"
            className="bg-black/40 border border-reveal-rose/25 rounded-full px-4 py-2.5 text-sm text-reveal-cream outline-none focus:border-reveal-rose transition-colors cursor-pointer"
          >
            <option value="all" className="bg-white text-black">All languages</option>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code} className="bg-white text-black">
                {l.flag} {l.name}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-reveal-rose/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="bg-reveal-burgundy/60 text-left text-reveal-rose uppercase text-xs tracking-wider">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Lang</th>
                  <th className="px-4 py-3 font-medium">Country</th>
                  <th className="px-4 py-3 font-medium">Referral</th>
                  <th className="px-4 py-3 font-medium">Signed up</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-reveal-cream/50">Loading...</td></tr>
                ) : entries.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-reveal-cream/50" data-testid="admin-empty">No signups found.</td></tr>
                ) : (
                  entries.map((e) => (
                    <tr key={e.id} className="border-t border-reveal-rose/10 hover:bg-white/[0.02]" data-testid={`admin-row-${e.id}`}>
                      <td className="px-4 py-3 text-reveal-cream">{e.name || "—"}</td>
                      <td className="px-4 py-3 text-reveal-cream/85">{e.email}</td>
                      <td className="px-4 py-3 text-reveal-cream/70 uppercase">{e.language || "—"}</td>
                      <td className="px-4 py-3 text-reveal-cream/70">{e.country || "—"}</td>
                      <td className="px-4 py-3 text-reveal-cream/70">{e.referral || "—"}</td>
                      <td className="px-4 py-3 text-reveal-cream/60 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-3">
                        <select
                          value={e.status}
                          onChange={(ev) => updateStatus(e.id, ev.target.value)}
                          data-testid={`admin-status-select-${e.id}`}
                          className={`rounded-full px-3 py-1 text-xs capitalize outline-none cursor-pointer border-0 ${statusStyle[e.status] || statusStyle.new}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="text-black bg-white">{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => remove(e.id, e.email)}
                          data-testid={`admin-delete-${e.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-reveal-cream/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
